import React, {createContext, useContext, useEffect, useState} from "react";
import axiosInstance from "../config/axios-config";
import {useTranslation} from "react-i18next";

const UserContext = createContext(null);

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error("useUser must be used within a UserProvider");
    }
    return context;
};

export const canViewTenantUsers = (tenantType, role) =>
    tenantType === "IMPORTER" && ["SYSTEM", "ADMIN", "OPERATOR"].includes(role);

export const UserProvider = ({children, userFullName, username, tenantName, tenantType, tenantId}) => {
    const [identity, setIdentity] = useState({role: null, loading: true, error: ""});
    const [identityVersion, setIdentityVersion] = useState(0);
    const email = username || userFullName;
    const scope = `${tenantType}:${tenantId}:${email}`;
    const needsTenantRole = ["IMPORTER", "PHARMACY"].includes(tenantType);
    const {i18n} = useTranslation();
    const language = i18n.language;

    useEffect(() => {
        const controller = new AbortController();
        setIdentity({scope, role: null, loading: needsTenantRole, error: ""});
        if (!needsTenantRole) {
            return () => controller.abort();
        }
        if (!email || !tenantId) {
            setIdentity({scope, role: null, loading: false, error: "CurrentUserLoadFailed"});
            return () => controller.abort();
        }
        // Both order participants need their database role; realm roles are not tenant-specific.
        axiosInstance.get("/api/v1/users", {params: {email}, signal: controller.signal, timeout: 10000,
            headers: {"Language-Context": language.toUpperCase()}})
            .then(({data}) => {
                if (controller.signal.aborted) {return;}
                const role = data.tenantId === tenantId && data.active !== false ? data.role : null;
                setIdentity({scope, role, loading: false, error: ""});
            })
            .catch(error => {
                if (!controller.signal.aborted) {
                    setIdentity({scope, role: null, loading: false, error: error.response?.data?.message || "CurrentUserLoadFailed"});
                }
            });
        return () => controller.abort();
    }, [email, tenantId, needsTenantRole, scope, language, identityVersion]);

    return (
        <UserContext.Provider value={{userFullName, tenantName, tenantType, tenantId, userIdentity: email,
            userRole: identity.scope === scope ? identity.role : null,
            userLoading: needsTenantRole && (identity.scope !== scope || identity.loading),
            userError: identity.scope === scope ? identity.error : "",
            reloadUser: () => setIdentityVersion(version => version + 1)}}>
            {children}
        </UserContext.Provider>
    );
};
